/**
 * Pure date utility functions for filter value formatting and parsing.
 *
 * These helpers convert between Date objects and the YYYY-MM-DD strings
 * expected by the Collins API (BookingFilters.php:37-38) and used in
 * collins-ui (bookings.js:610-611). They are intentionally timezone-safe
 * by operating on local date parts rather than UTC.
 */

/**
 * Formats a Date object to a YYYY-MM-DD string for use as a filter value.
 * Uses local date parts to avoid timezone boundary issues, matching the
 * convention used by the Collins API (BookingFilters.php:37-38) and
 * collins-ui (bookings.js:610-611).
 *
 * @param date - The Date object to format
 * @returns A string in the format "YYYY-MM-DD"
 */
export const formatDateToFilterValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Parses a YYYY-MM-DD filter string back to a Date object.
 * Sets time to noon to avoid timezone boundary issues, matching the
 * pattern used in DateSelector (DateSelector.tsx:99-106).
 *
 * @param value - A string in the format "YYYY-MM-DD"
 * @returns A Date object set to noon on the given date, or undefined if parsing fails
 */
export const parseFilterValueToDate = (value: string): Date | undefined => {
  const parts = value.split("-");
  if (parts.length !== 3) return undefined;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return undefined;
  return new Date(year, month, day, 12, 0, 0);
};
